import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Save, Trash2, Upload, Loader2, Move, Maximize2, Pencil, CheckCircle2 } from 'lucide-react';

/**
 * FlashingTraceEditor — draw-directly flashing template builder.
 *
 * Replaces the legacy numeric fold editor (2026-02-17). Operator clicks
 * to drop polyline vertices on a 5 mm grid; each click prompts for the
 * exact length in mm via an inline popover. Segments axis-snap to
 * horizontal/vertical by default (Shift to free-rotate). Optional image
 * underlay supports drag/scale/calibrate so the operator can trace over
 * a sketch or site photo at real-world scale.
 *
 * Output is the SAME `{ start_angle_deg, folds: [{length_mm, bend_angle,
 * bend_dir}] }` shape the existing FlashingProfileSVG + PDF generator
 * already consume, so no downstream changes are required.
 */
const GRID_MM = 5;                        // every grid square = 5 mm (user spec)
const GRID_MAJOR_EVERY = 10;              // bold line every 10 cells = 50 mm
const CANVAS_W = 720;
const CANVAS_H = 460;
const DEFAULT_PX_PER_MM = 1.4;            // initial zoom so a 500 mm flashing fits

// Built-in category seeds. The operator can extend this list at runtime
// via the "Add new…" option on the Category dropdown — the new categories
// are appended directly to the server-side `flashing_profile` dropdown so
// they appear for every appraisal + future template forever after.
const SEED_CATEGORIES = ['Apron', 'Barge', 'Box Gutter', 'Soaker', 'Valley', 'Parapet', 'Drip Edge', 'Back Tray', 'Custom'];

// Per-category STARTER shapes. When the operator picks a category on a
// fresh template (or hits "Load starter" later) the canvas is pre-loaded
// with a typical polyline for that flashing type — the operator just
// clicks each segment to retype the mm value. Saves redrawing the same
// pattern from scratch every time.
//
// Coordinates are in mm relative to the start point at (0,0). Each entry
// is a polyline expressed as an offset chain so it's easy to visualise:
//   first point: 0,0 (anchor)
//   each subsequent point: the absolute mm position from anchor
// The actual values were chosen to match common NZ roofing dimensions
// and are deliberately rounded to 50 mm so they snap cleanly to the grid.
const STARTER_SHAPES = {
  // 3-fold L: upstand → horizontal base → drip-edge
  'Apron':       [{ x_mm: 0, y_mm: 0 }, { x_mm: 0, y_mm: 100 }, { x_mm: 150, y_mm: 100 }, { x_mm: 150, y_mm: 75 }],
  // 3-fold downturn: upstand → across → kick-out down
  'Barge':       [{ x_mm: 0, y_mm: 0 }, { x_mm: 0, y_mm: 50 },  { x_mm: 150, y_mm: 50 },  { x_mm: 150, y_mm: 100 }],
  // 5-fold U for a 200 mm box gutter
  'Box Gutter':  [{ x_mm: 0, y_mm: 0 }, { x_mm: 0, y_mm: 100 }, { x_mm: 50, y_mm: 100 },  { x_mm: 50, y_mm: 250 }, { x_mm: 250, y_mm: 250 }, { x_mm: 250, y_mm: 100 }, { x_mm: 300, y_mm: 100 }, { x_mm: 300, y_mm: 0 }],
  // 3-fold zig (soaker behind a chimney etc.)
  'Soaker':      [{ x_mm: 0, y_mm: 0 }, { x_mm: 0, y_mm: 75 },  { x_mm: 200, y_mm: 75 },  { x_mm: 200, y_mm: 0 }],
  // 90° V
  'Valley':      [{ x_mm: 0, y_mm: 0 }, { x_mm: 100, y_mm: 100 }, { x_mm: 200, y_mm: 0 }],
  // Parapet cap: upstand, across, downstand, drip
  'Parapet':     [{ x_mm: 0, y_mm: 0 }, { x_mm: 0, y_mm: 100 }, { x_mm: 200, y_mm: 100 }, { x_mm: 200, y_mm: 0 }, { x_mm: 230, y_mm: 0 }],
  // 2-fold simple drip
  'Drip Edge':   [{ x_mm: 0, y_mm: 0 }, { x_mm: 50, y_mm: 0 },  { x_mm: 50, y_mm: 30 }],
  // Flat tray with shallow angled flicks at each end (Hot Water corrugate
  // profile pattern — total girth ~370 mm). Flat horizontal section at
  // the TOP, with small flicks dropping down-and-out at each end.
  // Operator-overridable via the "Save as starter for Back Tray" flow.
  'Back Tray':   [{ x_mm: 0, y_mm: 15 }, { x_mm: 25, y_mm: 0 }, { x_mm: 320, y_mm: 0 }, { x_mm: 345, y_mm: 15 }],
};

// Convert a 2-vector to its angle from horizontal in degrees, 0..360.
const vecAngle = (dx, dy) => {
  const a = (Math.atan2(dy, dx) * 180) / Math.PI;
  return ((a % 360) + 360) % 360;
};

// Snap a screen-mm value to the grid.
const snapMm = (mm) => Math.round(mm / GRID_MM) * GRID_MM;

// Compute folds[] + start_angle_deg from the operator's clicked points.
// pts is an array of {x_mm, y_mm} with the FIRST point being the start.
// For every interior vertex i (1..n-2) we measure the turn from the
// incoming vec (p[i-1]->p[i]) to the outgoing vec (p[i]->p[i+1]).
// The LAST segment doesn't have an outgoing turn — its bend_angle/dir
// are zeroed and ignored by the renderer.
function pointsToFolds(pts) {
  if (!pts || pts.length < 2) return { start_angle_deg: 0, folds: [] };
  const folds = [];
  const sx = pts[1].x_mm - pts[0].x_mm;
  const sy = pts[1].y_mm - pts[0].y_mm;
  const start_angle_deg = vecAngle(sx, sy);

  for (let i = 0; i < pts.length - 1; i++) {
    const dx = pts[i + 1].x_mm - pts[i].x_mm;
    const dy = pts[i + 1].y_mm - pts[i].y_mm;
    const length_mm = Math.round(Math.hypot(dx, dy));
    let bend_angle = 90;
    let bend_dir = 'cw';
    if (i < pts.length - 2) {
      const dx2 = pts[i + 2].x_mm - pts[i + 1].x_mm;
      const dy2 = pts[i + 2].y_mm - pts[i + 1].y_mm;
      // signed angle from (dx,dy) to (dx2,dy2)
      const cross = dx * dy2 - dy * dx2;
      const dot = dx * dx2 + dy * dy2;
      const signed = (Math.atan2(cross, dot) * 180) / Math.PI;
      bend_angle = Math.round(Math.abs(signed));
      bend_dir = signed >= 0 ? 'cw' : 'ccw';   // y-axis points DOWN on screen so +ve cross = CW visually
    }
    folds.push({ length_mm, bend_angle, bend_dir });
  }
  return { start_angle_deg: Math.round(start_angle_deg), folds };
}

// Inverse — render the existing template's folds back as clicked points so
// the editor can OPEN an existing template and let the operator tweak it.
function foldsToPoints(start_angle_deg, folds) {
  if (!folds || folds.length === 0) return [];
  let x = 0, y = 0;
  let heading = ((Number(start_angle_deg) || 0) * Math.PI) / 180;
  const pts = [{ x_mm: 0, y_mm: 0 }];
  folds.forEach((f) => {
    const len = Number(f.length_mm);
    x += Math.cos(heading) * len;
    y += Math.sin(heading) * len;
    pts.push({ x_mm: x, y_mm: y });
    const a = Number(f.bend_angle ?? 90);
    const sign = f.bend_dir === 'ccw' ? -1 : 1;
    heading += sign * ((a * Math.PI) / 180);
  });
  return pts;
}

// Axis-snap the proposed point relative to the anchor (last placed vertex).
// Picks whichever axis (horizontal or vertical) is closer to the cursor.
// `freeAngle` (computed from lineMode + Shift override) bypasses snap so
// the operator can draw kick-outs at arbitrary angles.
function axisSnap(anchor, target, freeAngle) {
  if (freeAngle) return { x_mm: snapMm(target.x_mm), y_mm: snapMm(target.y_mm) };
  const dx = Math.abs(target.x_mm - anchor.x_mm);
  const dy = Math.abs(target.y_mm - anchor.y_mm);
  if (dx >= dy) return { x_mm: snapMm(target.x_mm), y_mm: anchor.y_mm };
  return { x_mm: anchor.x_mm, y_mm: snapMm(target.y_mm) };
}

export default function FlashingTraceEditor({ initial, saving, onCancel, onSave, onSaveAsNew, onSetAsStarter, onUnsetAsStarter, categoryStarters, api }) {
  // ── Operator's clicked points (mm coords) ──────────────────────────
  const [points, setPoints] = useState(() => foldsToPoints(initial?.start_angle_deg, initial?.folds));
  // Track which segment popover (if any) is open.
  // segmentIndex = -1 means the popover is for the freshly-placed last point.
  const [popover, setPopover] = useState(null);     // { segmentIndex, length_mm, x, y }
  // Cursor preview (the candidate next-point) so the operator sees the
  // axis-snap line BEFORE clicking.
  const [hover, setHover] = useState(null);          // { x_mm, y_mm } or null
  const [shiftDown, setShiftDown] = useState(false);
  // Persistent line-mode toggle — `'axis'` snaps every new vertex to
  // perfectly horizontal/vertical relative to the previous one (good for
  // 90% of flashings), `'free'` lets the operator click anywhere for
  // kick-outs / drip-edges / non-square profiles. Shift acts as a
  // momentary override in EITHER mode (axis → free for one click, free
  // → axis when held).
  const [lineMode, setLineMode] = useState('axis');  // 'axis' | 'free'
  // Per-segment label position overrides. Each entry: { dx, dy } in
  // canvas-pixel offset from the segment mid-point (NOT mm, so it stays
  // relative to the drawn position regardless of zoom/pan). When the
  // operator drags a label badge, we record the override here; on save
  // the array gets persisted with the template so re-opening the same
  // profile keeps the operator's custom positions.
  const [labelOffsets, setLabelOffsets] = useState(() => {
    const seed = initial?.label_offsets;
    return Array.isArray(seed) ? seed.slice() : [];
  });
  // Drag state for label badges.
  const labelDragRef = useRef(null);
  // Vertex drag — operator can grab any vertex circle and reposition it
  // on the canvas; the polyline + auto-derived folds recompute live.
  const vertexDragRef = useRef(null);
  // Right-click reposition menu — { segIdx, x, y } in canvas pixels.
  // Lets the operator align a label cardinally (above / below / left /
  // right of the segment) instead of dragging — handy when laying out
  // several labels in a consistent row/column.
  const [labelMenu, setLabelMenu] = useState(null);
  // Drawing finished? When true, clicks on empty canvas are ignored so
  // the operator can save without adding stray vertices. Clicks on
  // existing segments still open the length popover for edits. Double-
  // click on the canvas or pressing Enter / the "Finish drawing"
  // toolbar button toggles this. Existing templates start finalized.
  const [finalized, setFinalized] = useState(() => (initial?.folds || []).length > 0);
  // Direction in which a click on the canvas extends the polyline. 'end'
  // (default) appends a new vertex after the last point. 'start' prepends
  // a new vertex before the first point — used when the operator needs
  // to draw additional folds back from the originating edge.
  const [drawFrom, setDrawFrom] = useState('end');

  // ── Image underlay ──────────────────────────────────────────────────
  const [image, setImage] = useState(null);  // { src, x_mm, y_mm, scale_mm_per_px, w, h }
  const [imageMode, setImageMode] = useState(null); // null | 'drag' | 'calibrate'
  const [calibPts, setCalibPts] = useState([]);     // [pt1, pt2] in mm

  // ── Metadata ────────────────────────────────────────────────────────
  const [name, setName] = useState(initial?.name || '');
  const [category, setCategory] = useState(initial?.category || 'Custom');
  // Sheet-profile pairing — ONE flashing can pair with MULTIPLE profiles
  // (e.g. a generic galv apron works for both Corrugate AND TrimRib).
  // Backed by the `sheet_profiles: string[]` field on the template doc;
  // legacy `sheet_profile` single-string is auto-coerced into a 1-item
  // array by the backend response normaliser.
  const [sheetProfilesPicked, setSheetProfilesPicked] = useState(
    Array.isArray(initial?.sheet_profiles)
      ? initial.sheet_profiles
      : (initial?.sheet_profile ? [initial.sheet_profile] : [])
  );
  // Universal-profile toggle — saves the operator from clicking every
  // chip when a template applies to every sheet profile. When true, the
  // per-profile list is ignored on save (backend treats this template
  // as a match for every profile).
  const [appliesToAllProfiles, setAppliesToAllProfiles] = useState(
    Boolean(initial?.applies_to_all_profiles)
  );
  // Pipe-usage auto-link rows — each entry tells the diagram annotator
  // "when the operator picks this pipe usage + diameter combo, suggest
  // THIS flashing template". Schema: { usage, pipe_dia_mm, length_default_m }
  const [pipeUsageLinks, setPipeUsageLinks] = useState(
    Array.isArray(initial?.pipe_usage_links) ? initial.pipe_usage_links : []
  );
  // Edging types — fetched live from `/api/edge-types` so operators can
  // add new types via the editor and see them everywhere immediately.
  // Falls back to SoftEdge-only if the API call fails / hasn't loaded
  // yet so the UI never breaks.
  const [edgeTypes, setEdgeTypes] = useState([
    { key: 'SoftEdge', label: 'SoftEdge (SE)', rate_per_m: 8.5 },
  ]);
  // Which fold indices (0-based) are "primary editable" on this template.
  // Template author sets these; appears as amber-highlighted F# chips
  // that operators must confirm dimensions on whenever this template is
  // instanced on an appraisal (pipe pin / library picker / etc.).
  const [primaryEditableFolds, setPrimaryEditableFolds] = useState(
    Array.isArray(initial?.primary_editable_folds) ? [...initial.primary_editable_folds] : []
  );
  const reloadEdgeTypes = useCallback(async () => {
    if (!api) return;
    try {
      const { data } = await api.get('/edge-types');
      if (Array.isArray(data) && data.length) setEdgeTypes(data);
    } catch (_) { /* fall back to seeded default */ }
  }, [api]);
  useEffect(() => { reloadEdgeTypes(); }, [reloadEdgeTypes]);

  const addEdgeType = useCallback(async () => {
    const raw = window.prompt('New edge type — display name (e.g. "Hemmed Edge"):');
    const label = (raw || '').trim();
    if (!label) return;
    // Derive a stable key from the label (alnum, no spaces). Operator can
    // tweak via the backend later if they want a different slug.
    const key = label.replace(/[^A-Za-z0-9]/g, '');
    if (!key) { alert('Edge type name must contain at least one letter or digit.'); return; }
    const rateRaw = window.prompt('Rate per meter ($/m):', '0');
    const rate = parseFloat(rateRaw || '0') || 0;
    try {
      const { data } = await api.post('/edge-types', {
        key, label,
        abbreviation: key.slice(0, 2).toUpperCase(),
        rate_per_m: rate,
      });
      // Refresh the registry + auto-select the new type on whichever end
      // last triggered the "+ Add" action. Simpler: just reload.
      await reloadEdgeTypes();
      return data.key;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to create edge type');
      return null;
    }
  }, [api, reloadEdgeTypes]);

  // Edging fitted at each polyline endpoint. `start` ↔ first vertex,
  // `end` ↔ last vertex. null = no edging on that end. Available types
  // are loaded from `/api/edge-types` (seeded with SoftEdge by default).
  const [edges, setEdges] = useState(() => {
    const e = initial?.edges || {};
    return {
      start: typeof e.start === 'string' && e.start.toLowerCase() !== 'none' ? e.start : null,
      end:   typeof e.end   === 'string' && e.end.toLowerCase()   !== 'none' ? e.end   : null,
    };
  });
  // Per-edge visual tuning (rotation / size / tail / under / back). UI
  // sliders write here; the SE glyph renderer reads from here. Empty
  // object {} means "use defaults" — so legacy templates keep rendering
  // exactly as they did before this feature shipped.
  const [edgeConfigs, setEdgeConfigs] = useState(() => {
    const ec = initial?.edge_configs || {};
    const sane = (v) => (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
    return { start: sane(ec.start), end: sane(ec.end) };
  });
  const [notes, setNotes] = useState(initial?.notes || '');
  // Auto-save bookkeeping. Only fires for EXISTING (saved) templates so
  // we never POST a half-named new template behind the operator's back.
  // `lastSavedAt` drives the "Saved 5s ago" badge; `autoSaveInFlight`
  // prevents overlapping PUTs while a save is still pending.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autoSaveInFlight, setAutoSaveInFlight] = useState(false);
  const [autoSaveSeq, setAutoSaveSeq] = useState(0); // bumped to skip first save on mount
  // Display unit for fold-length labels. Default is mm (the unit the
  // operator types into the popover). Switching to cm / m re-scales the
  // visible label only — the underlying length_mm data stays canonical
  // so PDFs + downstream calc keep working unchanged.
  const [displayUnit, setDisplayUnit] = useState('mm');  // 'mm' | 'cm' | 'm'
  const formatLen = (mm) => {
    const n = Number(mm) || 0;
    if (displayUnit === 'm')  return (n / 1000).toFixed(n < 1000 ? 3 : 2);
    if (displayUnit === 'cm') return (n / 10).toFixed(n % 10 === 0 ? 0 : 1);
    return Math.round(n).toString();
  };
  // Categories merge SEED_CATEGORIES with server-side `flashing_profile`
  // dropdown options so any custom category the operator has added in a
  // previous session persists across templates.
  const [categories, setCategories] = useState(() => [...SEED_CATEGORIES]);
  const [sheetProfiles, setSheetProfiles] = useState([]);

  // Pull fresh category + sheet-profile dropdowns on open. We dedup-merge
  // the server-side `flashing_profile` list (which can be edited by any
  // admin) with the built-in seeds so the operator sees a single
  // unified picker.
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/dropdowns');
        if (cancelled) return;
        const dd = r.data || {};
        const fp = Array.isArray(dd.flashing_profile) ? dd.flashing_profile : [];
        const merged = [...new Set([...SEED_CATEGORIES, ...fp])];
        setCategories(merged);
        setSheetProfiles(Array.isArray(dd.sheet_profile) ? dd.sheet_profile : []);
      } catch (_) { /* fallback to seeds */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Apply a category's starter shape to the canvas. Centres the polyline
  // roughly at the canvas mid-point so it's immediately visible without
  // panning. Snaps to the grid so dimensions read cleanly. Drops the
  // polyline into finalized state so the operator can immediately click
  // any segment to retype the mm value.
  //
  // Look-up priority: OPERATOR-DEFINED starter for that category (set via
  // "Save as starter for X" on a previously-saved template) → built-in
  // SEED_SHAPE fallback. This lets the office build their preferred
  // backtray once and have every future Back Tray load that shape.
  const loadStarter = (cat) => {
    let tpl = null;
    if (categoryStarters && categoryStarters[cat]) {
      const cs = categoryStarters[cat];
      tpl = foldsToPoints(cs.start_angle_deg, cs.folds);
      if (tpl.length < 2) tpl = null;
    }
    if (!tpl) tpl = STARTER_SHAPES[cat];
    if (!tpl) return false;
    // Translate so the polyline is centred on the current viewport
    const xs = tpl.map(p => p.x_mm);
    const ys = tpl.map(p => p.y_mm);
    const w = Math.max(1, Math.max(...xs) - Math.min(...xs));
    const h = Math.max(1, Math.max(...ys) - Math.min(...ys));
    const centerX = origin.x_mm + (CANVAS_W / pxPerMm) / 2;
    const centerY = origin.y_mm + (CANVAS_H / pxPerMm) / 2;
    const dx = centerX - (Math.min(...xs) + w / 2);
    const dy = centerY - (Math.min(...ys) + h / 2);
    const shifted = tpl.map(p => ({
      x_mm: snapMm(p.x_mm + dx),
      y_mm: snapMm(p.y_mm + dy),
    }));
    setPoints(shifted);
    setFinalized(true);
    setPopover(null);
    setHover(null);
    return true;
  };

  // Track whether the operator has touched the polyline yet. Used to
  // decide if a category switch should auto-load that category's
  // starter shape without a confirmation prompt.
  const polylineDirtyRef = useRef(false);
  useEffect(() => { polylineDirtyRef.current = points.length > 0; }, [points.length]);

  // ── Viewport ────────────────────────────────────────────────────────
  // Origin in mm coordinates (top-left corner of the canvas). Operator
  // can pan by dragging on empty canvas with image mode off.
  const [origin, setOrigin] = useState({ x_mm: 0, y_mm: 0 });
  const [pxPerMm, setPxPerMm] = useState(DEFAULT_PX_PER_MM);
  const svgRef = useRef(null);

  // Pan state
  const panRef = useRef(null);

  // Convert a DOM-event point to canvas-mm coords.
  const eventToMm = useCallback((evt) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = evt.clientX - rect.left;
    const py = evt.clientY - rect.top;
    return {
      x_mm: origin.x_mm + px / pxPerMm,
      y_mm: origin.y_mm + py / pxPerMm,
    };
  }, [origin, pxPerMm]);

  const mmToPx = useCallback((p) => ({
    px: (p.x_mm - origin.x_mm) * pxPerMm,
    py: (p.y_mm - origin.y_mm) * pxPerMm,
  }), [origin, pxPerMm]);

  // Shift-key tracking for the snap preview + Enter to finalise
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === 'Shift') setShiftDown(true);
      // Enter finishes drawing when the operator has at least 2 points
      // and isn't currently typing in an input (name / notes / popover).
      if (e.key === 'Enter') {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (points.length >= 2 && !finalized) {
          setFinalized(true);
          setPopover(null);
        }
      }
    };
    const onUp = (e) => { if (e.key === 'Shift') setShiftDown(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [points.length, finalized]);

  // Auto-center the existing polyline on first open.
  useEffect(() => {
    if (points.length < 2) return;
    fitToViewport(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // run once

  // Auto-fit on every points change so larger flashings (e.g. 1200 mm
  // backtrays, multi-fold box gutters) stay fully visible. Operator told
  // us the canvas is a *shape reference*, not a scale drawing — so we
  // re-zoom on every edit instead of letting segments fall off the page.
  // The wheel zoom + manual pan still work; this just makes "out of
  // view" geometry unreachable. Skipped when the operator is actively
  // typing in the popover so a half-typed length doesn't trigger a
  // visual snap.
  useEffect(() => {
    if (popover) return;
    if (points.length < 2) return;
    fitToViewport(points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  // Compute the zoom + origin that fits a polyline in the canvas with
  // padding. Shared by both the on-mount centre and the post-edit
  // auto-fit.
  function fitToViewport(pts) {
    const xs = pts.map(p => p.x_mm);
    const ys = pts.map(p => p.y_mm);
    const w = Math.max(1, Math.max(...xs) - Math.min(...xs));
    const h = Math.max(1, Math.max(...ys) - Math.min(...ys));
    // Generous padding for the perpendicular label pills + the H/V snap
    // toggle / drawing-mode indicators around the canvas edges.
    const PAD_PX = 70;
    const fit = Math.min((CANVAS_W - PAD_PX * 2) / w, (CANVAS_H - PAD_PX * 2) / h);
    const newScale = Math.min(4, Math.max(0.15, fit));
    setPxPerMm(newScale);
    setOrigin({
      x_mm: Math.min(...xs) - ((CANVAS_W / newScale) - w) / 2,
      y_mm: Math.min(...ys) - ((CANVAS_H / newScale) - h) / 2,
    });
  }

  // ── Canvas mouse handlers ──────────────────────────────────────────
  const onCanvasClick = (e) => {
    if (popover) return;          // popover-open clicks routed elsewhere
    // Once drawing is finalised, new-point clicks are ignored so the
    // operator can save / type details without accidentally extending
    // the polyline. Click an existing segment to edit its length.
    if (finalized && imageMode !== 'calibrate') return;
    const target = eventToMm(e);
    // Image calibration: capture two clicks, then prompt for the known mm.
    if (imageMode === 'calibrate') {
      const nextCalib = [...calibPts, target];
      if (nextCalib.length === 2) {
        const [a, b] = nextCalib;
        const px_distance = Math.hypot(b.x_mm - a.x_mm, b.y_mm - a.y_mm) * pxPerMm;
        const raw = window.prompt('Known real-world distance between the two clicks (mm):', '500');
        const real = parseFloat(raw);
        if (real && real > 0 && image && px_distance > 0) {
          const newScale = real / px_distance;   // mm per image-px
          setImage({ ...image, scale_mm_per_px: newScale });
        }
        setCalibPts([]);
        setImageMode(null);
      } else {
        setCalibPts(nextCalib);
      }
      return;
    }
    // First click — drop the start point
    if (points.length === 0) {
      const snapped = { x_mm: snapMm(target.x_mm), y_mm: snapMm(target.y_mm) };
      setPoints([snapped]);
      return;
    }
    // Subsequent clicks — axis-snap, then open the length popover. The
    // anchor is the LAST vertex when extending from the end, or the
    // FIRST vertex when extending from the start (drawFrom === 'start').
    const anchor = drawFrom === 'start' ? points[0] : points[points.length - 1];
    // Resolve free-angle: persistent mode XOR Shift override.
    // axis mode + no shift  → snap to H/V
    // axis mode +    shift  → free
    // free mode + no shift  → free
    // free mode +    shift  → snap to H/V (momentary)
    const freeAngle = (lineMode === 'free') ? !e.shiftKey : e.shiftKey;
    const snapped = axisSnap(anchor, target, freeAngle);
    const length_mm = snapMm(Math.round(Math.hypot(snapped.x_mm - anchor.x_mm, snapped.y_mm - anchor.y_mm)));
    if (length_mm <= 0) return;     // refuse zero-length click
    let nextPoints;
    let segmentIndex;
    if (drawFrom === 'start') {
      // Prepend → the new point becomes the new vertex 0; the old
      // vertex 0 stays in place as vertex 1; new segment is at idx 0.
      // labelOffsets need to shift right by 1 to keep alignment.
      nextPoints = [snapped, ...points];
      setLabelOffsets(prev => [{ dx: 0, dy: 0 }, ...prev]);
      // primary_editable_folds reference fold indices — shift them too
      // so user's "F2 editable" stays pinned to the same physical fold.
      setPrimaryEditableFolds(prev => prev.map(i => i + 1));
      segmentIndex = 0;
    } else {
      nextPoints = [...points, snapped];
      segmentIndex = points.length - 1;
    }
    setPoints(nextPoints);
    // Open the popover anchored at the screen pos of the new point
    const { px, py } = mmToPx(snapped);
    setPopover({
      segmentIndex,
      length_mm,
      x: px,
      y: py,
    });
  };

  const onCanvasMove = (e) => {
    // Pan in progress?
    if (panRef.current) {
      const dx = (e.clientX - panRef.current.startClient.x) / pxPerMm;
      const dy = (e.clientY - panRef.current.startClient.y) / pxPerMm;
      setOrigin({
        x_mm: panRef.current.startOrigin.x_mm - dx,
        y_mm: panRef.current.startOrigin.y_mm - dy,
      });
      return;
    }
    if (popover || finalized) { setHover(null); return; }
    const target = eventToMm(e);
    if (points.length === 0) {
      setHover({ x_mm: snapMm(target.x_mm), y_mm: snapMm(target.y_mm) });
    } else {
      const anchor = drawFrom === 'start' ? points[0] : points[points.length - 1];
      const freeAngle = (lineMode === 'free') ? !e.shiftKey : e.shiftKey;
      setHover(axisSnap(anchor, target, freeAngle));
    }
  };

  // Right-click removes the most recently placed point in whichever
  // direction the operator is drawing from + closes any popover.
  const onContextMenu = (e) => {
    e.preventDefault();
    setPopover(null);
    if (drawFrom === 'start' && points.length > 0) {
      setPoints((p) => p.slice(1));
      setLabelOffsets(prev => prev.slice(1));
      setPrimaryEditableFolds(prev => prev.map(i => i - 1).filter(i => i >= 0));
    } else {
      setPoints((p) => p.slice(0, -1));
    }
  };

  // Apply the length entered in the popover to recompute the just-placed
  // vertex's coords relative to the previous anchor — keeps the bend
  // direction the operator drew but locks the exact mm distance.
  const applyPopoverLength = () => {
    if (!popover) return;
    const idx = popover.segmentIndex;
    const anchor = points[idx];
    const target = points[idx + 1];
    if (!anchor || !target) { setPopover(null); return; }
    const dx = target.x_mm - anchor.x_mm;
    const dy = target.y_mm - anchor.y_mm;
    const oldLen = Math.hypot(dx, dy);
    if (oldLen <= 0) { setPopover(null); return; }
    const newLen = snapMm(parseFloat(popover.length_mm) || 0);
    if (newLen <= 0) { setPopover(null); return; }
    const ratio = newLen / oldLen;
    const newTarget = {
      x_mm: anchor.x_mm + dx * ratio,
      y_mm: anchor.y_mm + dy * ratio,
    };
    // Apply the delta to ALL subsequent points so the rest of the
    // polyline slides along with the resized segment (otherwise editing
    // an earlier segment would break the geometry).
    const delta = { x: newTarget.x_mm - target.x_mm, y: newTarget.y_mm - target.y_mm };
    const next = points.map((p, i) => i <= idx ? p : {
      x_mm: p.x_mm + delta.x,
      y_mm: p.y_mm + delta.y,
    });
    setPoints(next);
    setPopover(null);
  };

  // Click on an existing segment opens its popover for length editing.
  const onSegmentClick = (segIdx, evt) => {
    evt.stopPropagation();
    const anchor = points[segIdx];
    const next   = points[segIdx + 1];
    const length_mm = Math.round(Math.hypot(next.x_mm - anchor.x_mm, next.y_mm - anchor.y_mm));
    const mid = mmToPx({ x_mm: (anchor.x_mm + next.x_mm) / 2, y_mm: (anchor.y_mm + next.y_mm) / 2 });
    setPopover({ segmentIndex: segIdx, length_mm, x: mid.px, y: mid.py });
  };

  // Image upload
  const onImageFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Place the image so it fills the canvas at the current scale.
        const desired_w_mm = (CANVAS_W * 0.6) / pxPerMm;
        const scale_mm_per_px = desired_w_mm / img.width;
        setImage({
          src: reader.result,
          x_mm: origin.x_mm + (CANVAS_W / pxPerMm - img.width * scale_mm_per_px) / 2,
          y_mm: origin.y_mm + (CANVAS_H / pxPerMm - img.height * scale_mm_per_px) / 2,
          scale_mm_per_px,
          w: img.width,
          h: img.height,
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  // Image drag — separate handler so it doesn't interfere with polyline drawing.
  const imgDragRef = useRef(null);
  const onImagePointerDown = (e) => {
    if (imageMode !== 'drag') return;
    e.stopPropagation();
    const start = eventToMm(e);
    imgDragRef.current = { startMm: start, startImage: { ...image } };
    window.addEventListener('pointermove', onImagePointerMove);
    window.addEventListener('pointerup', onImagePointerUp);
  };
  const onImagePointerMove = (e) => {
    if (!imgDragRef.current) return;
    const cur = eventToMm(e);
    setImage((im) => im && ({
      ...im,
      x_mm: imgDragRef.current.startImage.x_mm + (cur.x_mm - imgDragRef.current.startMm.x_mm),
      y_mm: imgDragRef.current.startImage.y_mm + (cur.y_mm - imgDragRef.current.startMm.y_mm),
    }));
  };
  const onImagePointerUp = () => {
    imgDragRef.current = null;
    window.removeEventListener('pointermove', onImagePointerMove);
    window.removeEventListener('pointerup', onImagePointerUp);
  };

  // Canvas pan when no other mode is active and click on EMPTY canvas
  const onCanvasPointerDown = (e) => {
    if (popover || imageMode || points.length > 0) return;     // only pan when polyline empty (rare)
    // Middle-mouse pan (always)
    if (e.button === 1) {
      panRef.current = {
        startClient: { x: e.clientX, y: e.clientY },
        startOrigin: { ...origin },
      };
    }
  };
  const onCanvasPointerUp = () => { panRef.current = null; };

  // ── Render helpers ─────────────────────────────────────────────────
  const { start_angle_deg, folds } = pointsToFolds(points);
  const girth = folds.reduce((s, f) => s + (Number(f.length_mm) || 0), 0);

  const canSave = name.trim() && points.length >= 2;

  // Build the API payload from current state — single source of truth so
  // the explicit Save / auto-save / Save-as-new code paths can't drift.
  const buildPayload = () => ({
    _id: initial?._id,
    name: name.trim(),
    category,
    sheet_profiles: appliesToAllProfiles ? [] : sheetProfilesPicked,
    applies_to_all_profiles: appliesToAllProfiles,
    pipe_usage_links: pipeUsageLinks,
    edges,
    edge_configs: edgeConfigs,
    primary_editable_folds: primaryEditableFolds,
    notes: notes.trim(),
    start_angle_deg,
    folds,
    label_offsets: labelOffsets,
  });

  // ── Auto-save (existing templates only) ─────────────────────────────
  // Debounce 1500 ms. Watches every field that ends up in the payload.
  // Skips: (1) brand-new (no _id) templates because they need an
  // explicit Create + name first, (2) drawings still in progress
  // (`finalized=false`) to avoid persisting half-traced shapes,
  // (3) names that haven't been set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initial?._id) return; // never auto-save a new template
    if (!finalized) return;
    if (!name.trim()) return;
    if (autoSaveSeq === 0) { setAutoSaveSeq(1); return; } // skip mount
    const t = setTimeout(async () => {
      if (autoSaveInFlight) return;
      setAutoSaveInFlight(true);
      try {
        const payload = buildPayload();
        // Direct PUT — auto-save bypasses the parent's onSave so the
        // modal doesn't close. Page parent will re-sync via its own
        // listing query.
        if (api) {
          await api.put(`/flashing-templates/${initial._id}`, payload);
          setLastSavedAt(new Date());
        }
      } catch (_) {
        /* swallow — manual save still available */
      } finally {
        setAutoSaveInFlight(false);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [name, category, JSON.stringify(sheetProfilesPicked), appliesToAllProfiles, JSON.stringify(pipeUsageLinks), JSON.stringify(edges), JSON.stringify(edgeConfigs), JSON.stringify(primaryEditableFolds), notes, JSON.stringify(folds), JSON.stringify(labelOffsets), finalized]);

  // Grid lines visible in the current viewport (subset).
  const renderGrid = () => {
    const lines = [];
    const startX_mm = Math.floor(origin.x_mm / GRID_MM) * GRID_MM;
    const startY_mm = Math.floor(origin.y_mm / GRID_MM) * GRID_MM;
    const endX_mm   = origin.x_mm + CANVAS_W / pxPerMm;
    const endY_mm   = origin.y_mm + CANVAS_H / pxPerMm;
    for (let x = startX_mm; x <= endX_mm; x += GRID_MM) {
      const major = Math.round(x / GRID_MM) % GRID_MAJOR_EVERY === 0;
      const px = (x - origin.x_mm) * pxPerMm;
      lines.push(<line key={`vx${x}`} x1={px} y1={0} x2={px} y2={CANVAS_H} stroke={major ? '#CBD5E1' : '#E2E8F0'} strokeWidth={major ? 0.8 : 0.4} />);
    }
    for (let y = startY_mm; y <= endY_mm; y += GRID_MM) {
      const major = Math.round(y / GRID_MM) % GRID_MAJOR_EVERY === 0;
      const py = (y - origin.y_mm) * pxPerMm;
      lines.push(<line key={`hy${y}`} x1={0} y1={py} x2={CANVAS_W} y2={py} stroke={major ? '#CBD5E1' : '#E2E8F0'} strokeWidth={major ? 0.8 : 0.4} />);
    }
    return lines;
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="flashing-trace-editor">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[96vh] flex flex-col">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-bold text-[#0F172A]">
            {initial?._id ? 'Edit flashing template' : 'Trace new flashing template'}
            <span className="ml-2 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">5 mm grid</span>
          </h2>
          <button onClick={onCancel} className="p-1 text-slate-500 hover:text-slate-700" data-testid="flashing-trace-close"><X size={18} /></button>
        </header>

        <div className="flex-1 overflow-auto p-4 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
          {/* CANVAS COLUMN */}
          <div>
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap pb-2 text-xs">
              {/* Persistent line-mode toggle — primary control for
                  horizontal/vertical snap vs free-angle drawing. Shift
                  acts as a momentary inverse. */}
              <div className="inline-flex rounded ring-1 ring-slate-200 overflow-hidden" data-testid="flashing-trace-mode">
                <button
                  type="button"
                  data-testid="flashing-trace-mode-axis"
                  onClick={() => setLineMode('axis')}
                  className={`inline-flex items-center gap-1 px-2 py-1 transition-all ${lineMode === 'axis' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  title="Snap every new vertex to horizontal or vertical (most flashings)"
                >
                  <span className="font-bold tracking-wide">H/V snap</span>
                </button>
                <button
                  type="button"
                  data-testid="flashing-trace-mode-free"
                  onClick={() => setLineMode('free')}
                  className={`inline-flex items-center gap-1 px-2 py-1 border-l border-slate-200 transition-all ${lineMode === 'free' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  title="Draw at any angle (kick-outs, drip-edges, non-square profiles)"
                >
                  <span className="font-bold tracking-wide">Free angle</span>
                </button>
              </div>

              <label data-testid="flashing-trace-upload" className="inline-flex items-center gap-1.5 px-2 py-1 rounded ring-1 ring-slate-200 hover:bg-slate-50 cursor-pointer">
                <Upload size={12} /> Upload sketch / photo
                <input type="file" accept="image/*" onChange={onImageFile} className="hidden" />
              </label>
              {/* Drawing-state toggle. Becomes the dominant CTA once the
                  operator has at least 2 points — they need to click this
                  (or double-click the canvas / press Enter) to lock the
                  polyline so they can save without adding stray points. */}
              {points.length >= 2 && (
                finalized ? (
                  <>
                    <button
                      type="button"
                      data-testid="flashing-trace-resume"
                      onClick={() => { setFinalized(false); setDrawFrom('end'); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded ring-1 bg-white ring-slate-200 hover:bg-slate-50 text-slate-700"
                      title="Add more points to the end of the polyline"
                    >
                      <Pencil size={12} /> Extend from end
                    </button>
                    <button
                      type="button"
                      data-testid="flashing-trace-extend-start"
                      onClick={() => { setFinalized(false); setDrawFrom('start'); }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded ring-1 bg-white ring-emerald-300 hover:bg-emerald-50 text-emerald-700"
                      title="Add more points BEFORE the first vertex (prepend to the polyline)"
                    >
                      <Pencil size={12} /> Extend from start
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="flashing-trace-finish"
                      onClick={() => { setFinalized(true); setPopover(null); setHover(null); setDrawFrom('end'); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 font-semibold shadow-sm"
                      title="Lock the polyline so you can save (or double-click the canvas / press Enter)"
                    >
                      <CheckCircle2 size={12} /> Finish drawing
                    </button>
                    {/* Mid-draw indicator — shows which end clicks extend from */}
                    <div className="inline-flex rounded ring-1 ring-slate-200 overflow-hidden text-[10px]" data-testid="flashing-trace-draw-from">
                      <button
                        type="button"
                        data-testid="flashing-trace-draw-from-end"
                        onClick={() => setDrawFrom('end')}
                        className={`px-2 py-1 ${drawFrom === 'end' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        title="Next click extends from the LAST vertex"
                      >Draw → end</button>
                      <button
                        type="button"
                        data-testid="flashing-trace-draw-from-start"
                        onClick={() => setDrawFrom('start')}
                        className={`px-2 py-1 border-l border-slate-200 ${drawFrom === 'start' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        title="Next click prepends BEFORE the FIRST vertex"
                      >start ← Draw</button>
                    </div>
                  </>
                )
              )}
              {image && (
                <>
                  <button type="button" data-testid="flashing-trace-img-drag" onClick={() => setImageMode(m => m === 'drag' ? null : 'drag')} className={`inline-flex items-center gap-1 px-2 py-1 rounded ring-1 ${imageMode === 'drag' ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white ring-slate-200 hover:bg-slate-50'}`}><Move size={12} /> Drag</button>
                  <button type="button" data-testid="flashing-trace-img-calibrate" onClick={() => { setImageMode(m => m === 'calibrate' ? null : 'calibrate'); setCalibPts([]); }} className={`inline-flex items-center gap-1 px-2 py-1 rounded ring-1 ${imageMode === 'calibrate' ? 'bg-amber-600 text-white ring-amber-600' : 'bg-white ring-slate-200 hover:bg-slate-50'}`}><Maximize2 size={12} /> Calibrate</button>
                  <button type="button" onClick={() => setImage(null)} className="inline-flex items-center gap-1 px-2 py-1 rounded ring-1 ring-rose-200 text-rose-700 hover:bg-rose-50"><Trash2 size={12} /> Clear image</button>
                </>
              )}
              <span className="ml-auto text-[10px] text-slate-500">
                <kbd className="px-1 bg-slate-100 ring-1 ring-slate-200 rounded">Shift</kbd> = inverse mode ·
                <kbd className="px-1 bg-slate-100 ring-1 ring-slate-200 rounded ml-1">Right-click</kbd> = undo last point ·
                <kbd className="px-1 bg-slate-100 ring-1 ring-slate-200 rounded ml-1">Wheel</kbd> = zoom
              </span>
              {/* Display-unit picker — switches the label format only.
                  The underlying length_mm data stays canonical so PDFs
                  + downstream tradespack/quoting code keep working. */}
              <div className="inline-flex rounded ring-1 ring-slate-200 overflow-hidden" data-testid="flashing-trace-unit">
                <span className="px-1.5 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-[0.16em]">Units</span>
                {['mm', 'cm', 'm'].map(u => (
                  <button
                    key={u}
                    type="button"
                    data-testid={`flashing-trace-unit-${u}`}
                    onClick={() => setDisplayUnit(u)}
                    className={`px-2 py-1 border-l border-slate-200 text-[11px] font-bold ${displayUnit === u ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >{u}</button>
                ))}
              </div>
            </div>

            {/* SVG canvas */}
            <div className="border border-slate-300 rounded-md bg-slate-50/40 overflow-hidden" style={{ width: CANVAS_W, height: CANVAS_H, maxWidth: '100%' }}>
              <svg
                ref={svgRef}
                width={CANVAS_W}
                height={CANVAS_H}
                viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
                onClick={onCanvasClick}
                onDoubleClick={() => { if (points.length >= 2 && !finalized) { setFinalized(true); setPopover(null); } }}
                onMouseMove={onCanvasMove}
                onMouseLeave={() => setHover(null)}
                onContextMenu={onContextMenu}
                onPointerDown={onCanvasPointerDown}
                onPointerUp={onCanvasPointerUp}
                onWheel={(e) => {
                  e.preventDefault();
                  const factor = e.deltaY > 0 ? 0.9 : 1.1;
                  setPxPerMm(s => Math.min(8, Math.max(0.3, s * factor)));
                }}
                data-testid="flashing-trace-canvas"
                style={{ cursor: imageMode === 'drag' ? 'move' : (imageMode === 'calibrate' ? 'crosshair' : (finalized ? 'default' : 'crosshair')) }}
              >
                {/* Grid */}
                {renderGrid()}
                {/* Image underlay */}
                {image && (
                  <image
                    href={image.src}
                    x={(image.x_mm - origin.x_mm) * pxPerMm}
                    y={(image.y_mm - origin.y_mm) * pxPerMm}
                    width={image.w * image.scale_mm_per_px * pxPerMm}
                    height={image.h * image.scale_mm_per_px * pxPerMm}
                    opacity={0.4}
                    style={{ pointerEvents: imageMode === 'drag' ? 'all' : 'none', cursor: imageMode === 'drag' ? 'move' : 'default' }}
                    onPointerDown={onImagePointerDown}
                    data-testid="flashing-trace-image"
                  />
                )}
                {/* Polyline */}
                {points.length >= 2 && (
                  <>
                    {points.slice(0, -1).map((p, i) => {
                      const a = mmToPx(p);
                      const b = mmToPx(points[i + 1]);
                      const segLen = Math.round(Math.hypot(points[i + 1].x_mm - p.x_mm, points[i + 1].y_mm - p.y_mm));
                      const cx = (a.px + b.px) / 2;
                      const cy = (a.py + b.py) / 2;
                      // Auto-position: perpendicular to the segment on
                      // the side facing AWAY from the polyline centroid,
                      // 32 px clear of the line. Operator can drag this
                      // anywhere — the drag delta is stored as { dx, dy }
                      // in labelOffsets[i] and ADDS to the auto-position.
                      const vx = b.px - a.px;
                      const vy = b.py - a.py;
                      const segPx = Math.max(1, Math.hypot(vx, vy));
                      const nx = -vy / segPx;
                      const ny =  vx / segPx;
                      const cents = points.reduce((acc, pt) => {
                        const sp = mmToPx(pt);
                        return [acc[0] + sp.px, acc[1] + sp.py];
                      }, [0, 0]);
                      const gx = cents[0] / points.length;
                      const gy = cents[1] / points.length;
                      const toG_x = cx - gx;
                      const toG_y = cy - gy;
                      const sign = (nx * toG_x + ny * toG_y) >= 0 ? 1 : -1;
                      const AUTO_OFF = 32;
                      const autoLx = cx + sign * nx * AUTO_OFF;
                      const autoLy = cy + sign * ny * AUTO_OFF;
                      const ov = labelOffsets[i] || { dx: 0, dy: 0 };
                      let lx = autoLx + ov.dx;
                      let ly = autoLy + ov.dy;
                      // Two-line label: F-name on top, measurement below.
                      // Width sized to the longer of the two strings.
                      const measureText = `${formatLen(segLen)}${displayUnit === 'mm' ? '' : ' ' + displayUnit}`;
                      const fNameText = `F${i + 1}`;
                      const longer = Math.max(fNameText.length, measureText.length);
                      const pillW = 16 + longer * 7.2;
                      const pillH = 34;
                      // Clamp inside the canvas
                      lx = Math.max(pillW / 2 + 2, Math.min(CANVAS_W - pillW / 2 - 2, lx));
                      ly = Math.max(pillH / 2 + 2, Math.min(CANVAS_H - pillH / 2 - 2, ly));
                      // Drag handlers — pointer down captures the badge,
                      // moves track the delta and update labelOffsets[i].
                      const onLabelDown = (ev) => {
                        ev.stopPropagation();
                        labelDragRef.current = {
                          idx: i,
                          startClient: { x: ev.clientX, y: ev.clientY },
                          startOff:    { dx: ov.dx, dy: ov.dy },
                          moved: false,
                        };
                        const onMove = (e2) => {
                          if (!labelDragRef.current) return;
                          const dx = e2.clientX - labelDragRef.current.startClient.x;
                          const dy = e2.clientY - labelDragRef.current.startClient.y;
                          if (Math.hypot(dx, dy) > 3) labelDragRef.current.moved = true;
                          setLabelOffsets(prev => {
                            const next = prev.slice();
                            next[i] = {
                              dx: labelDragRef.current.startOff.dx + dx,
                              dy: labelDragRef.current.startOff.dy + dy,
                            };
                            return next;
                          });
                        };
                        const onUp = (e2) => {
                          window.removeEventListener('pointermove', onMove);
                          window.removeEventListener('pointerup', onUp);
                          // If the operator didn't actually drag — count
                          // it as a click and open the length popover.
                          if (labelDragRef.current && !labelDragRef.current.moved) {
                            onSegmentClick(i, e2);
                          }
                          labelDragRef.current = null;
                        };
                        window.addEventListener('pointermove', onMove);
                        window.addEventListener('pointerup', onUp);
                      };
                      return (
                        <g key={`s${i}`} data-testid={`flashing-trace-seg-${i}`}>
                          {/* Invisible thicker line for easier click target */}
                          <line x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke="#0F766E" strokeWidth={16} opacity={0} onClick={(ev) => onSegmentClick(i, ev)} style={{ cursor: 'pointer' }} />
                          {/* Actual rendered line */}
                          <line x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke="#16A34A" strokeWidth={3.5} strokeLinecap="round" pointerEvents="none" />
                          {/* Faint leader line from segment mid-point to the label badge */}
                          <line x1={cx} y1={cy} x2={lx} y2={ly} stroke="#94A3B8" strokeWidth={0.8} strokeDasharray="2 2" pointerEvents="none" />
                          {/* Two-tone label — F-name on top, measurement below.
                              The whole pill is draggable; clicking (no drag) opens the popover.
                              Right-click reveals an auto-reposition menu. */}
                          <g style={{ cursor: 'grab' }} onPointerDown={onLabelDown} onContextMenu={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            // Position the menu at the badge top-right
                            // corner so it doesn't fight with the badge itself.
                            const rect = svgRef.current.getBoundingClientRect();
                            setLabelMenu({
                              segIdx: i,
                              x: ev.clientX - rect.left,
                              y: ev.clientY - rect.top,
                              autoLx,
                              autoLy,
                              cx, cy,
                            });
                          }} data-testid={`flashing-trace-seg-label-${i}`}>
                            <rect x={lx - pillW / 2} y={ly - pillH / 2} width={pillW} height={pillH} rx={8} fill="#FFFFFF" stroke="#16A34A" strokeWidth={1.5} />
                            {/* F-name divider */}
                            <line x1={lx - pillW / 2 + 6} y1={ly - 1} x2={lx + pillW / 2 - 6} y2={ly - 1} stroke="#D1FAE5" strokeWidth={1} pointerEvents="none" />
                            <text x={lx} y={ly - 4} textAnchor="middle" fontSize={11} fontWeight={800} fill="#15803D" pointerEvents="none">{fNameText}</text>
                            <text x={lx} y={ly + 12} textAnchor="middle" fontSize={12} fontWeight={700} fill="#0F172A" pointerEvents="none">{measureText}</text>
                          </g>
                        </g>
                      );
                    })}
                  </>
                )}
                {/* Vertex dots — draggable when the polyline is finalised
                    so the operator can fine-tune positions without
                    redrawing. Pointer-down captures the vertex, mouse
                    moves update points[i] in real time (snapped to grid). */}
                {points.map((p, i) => {
                  const { px, py } = mmToPx(p);
                  const onVertexDown = (ev) => {
                    if (!finalized) return; // mid-draw clicks still go to canvas
                    ev.stopPropagation();
                    ev.preventDefault();
                    vertexDragRef.current = {
                      idx: i,
                      moved: false,
                      startClient: { x: ev.clientX, y: ev.clientY },
                    };
                    const onMove = (e2) => {
                      if (!vertexDragRef.current) return;
                      const target = eventToMm(e2);
                      const snapped = { x_mm: snapMm(target.x_mm), y_mm: snapMm(target.y_mm) };
                      if (Math.hypot(e2.clientX - vertexDragRef.current.startClient.x,
                                     e2.clientY - vertexDragRef.current.startClient.y) > 3) {
                        vertexDragRef.current.moved = true;
                      }
                      setPoints(prev => prev.map((q, j) => j === vertexDragRef.current.idx ? snapped : q));
                    };
                    const onUp = () => {
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                      vertexDragRef.current = null;
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                  };
                  return (
                    <g key={`v${i}`}>
                      {/* Larger transparent hit-area for easier grabbing */}
                      <circle
                        cx={px} cy={py} r={12}
                        fill="transparent"
                        onPointerDown={onVertexDown}
                        style={{ cursor: finalized ? 'grab' : 'crosshair' }}
                        data-testid={`flashing-trace-vertex-${i}`}
                      />
                      <circle
                        cx={px} cy={py} r={finalized ? 6 : 5}
                        fill={i === 0 ? '#0F766E' : '#16A34A'}
                        stroke="#fff" strokeWidth={1.5}
                        pointerEvents="none"
                      />
                      {finalized && (
                        <circle
                          cx={px} cy={py} r={9}
                          fill="none"
                          stroke={i === 0 ? '#0F766E' : '#16A34A'}
                          strokeWidth={1}
                          strokeDasharray="2 2"
                          opacity={0.6}
                          pointerEvents="none"
                        />
                      )}
                    </g>
                  );
                })}
                {/* Edging glyphs at polyline endpoints — drawn AFTER vertex
                    dots so the curl sits visibly on top. `start` uses the
                    1st vertex + 2nd vertex as the tangent reference;
                    `end` uses the last + 2nd-to-last. */}
                {finalized && points.length >= 2 && edges?.start && (() => {
                  const a = mmToPx(points[0]);
                  const b = mmToPx(points[1]);
                  return (
                    <EdgeGlyphInline
                      end="start"
                      edgeType={edges.start}
                      x0={a.px} y0={a.py}
                      xa={b.px} ya={b.py}
                      config={edgeConfigs?.start || {}}
                    />
                  );
                })()}
                {finalized && points.length >= 2 && edges?.end && (() => {
                  const last = points.length - 1;
                  const a = mmToPx(points[last]);
                  const b = mmToPx(points[last - 1]);
                  return (
                    <EdgeGlyphInline
                      end="end"
                      edgeType={edges.end}
                      x0={a.px} y0={a.py}
                      xa={b.px} ya={b.py}
                      config={edgeConfigs?.end || {}}
                    />
                  );
                })()}
                {/* Hover preview line */}
                {hover && points.length > 0 && !popover && (
                  <>
                    {(() => {
                      const anchor = drawFrom === 'start' ? points[0] : points[points.length - 1];
                      const a = mmToPx(anchor);
                      const b = mmToPx(hover);
                      const len = Math.round(Math.hypot(hover.x_mm - anchor.x_mm, hover.y_mm - anchor.y_mm));
                      return (
                        <g>
                          <line x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke="#16A34A" strokeWidth={2} strokeDasharray="4 4" opacity={0.7} />
                          <circle cx={b.px} cy={b.py} r={4} fill="#16A34A" opacity={0.4} />
                          <text x={(a.px + b.px) / 2} y={(a.py + b.py) / 2 - 8} textAnchor="middle" fontSize={11} fill="#0F766E" style={{ paintOrder: 'stroke', stroke: '#FFF', strokeWidth: 2.4 }}>
                            {formatLen(len)} {displayUnit} {((lineMode === 'free') ? !shiftDown : shiftDown) ? '(free)' : '(H/V)'}
                          </text>
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Calibration markers */}
                {imageMode === 'calibrate' && calibPts.map((p, i) => {
                  const { px, py } = mmToPx(p);
                  return <g key={`cal${i}`}><circle cx={px} cy={py} r={6} fill="#F59E0B" /><text x={px + 8} y={py + 4} fontSize={11} fill="#92400E">{i + 1}</text></g>;
                })}
                {/* Empty state */}
                {points.length === 0 && !image && (
                  <g>
                    <rect x={CANVAS_W / 2 - 180} y={CANVAS_H / 2 - 28} width={360} height={56} rx={10} fill="#FFF" opacity={0.85} stroke="#CBD5E1" />
                    <text x={CANVAS_W / 2} y={CANVAS_H / 2 - 6} textAnchor="middle" fontSize={13} fontWeight={600} fill="#475569">Click on the grid to drop the start point</text>
                    <text x={CANVAS_W / 2} y={CANVAS_H / 2 + 14} textAnchor="middle" fontSize={11} fill="#64748B">
                      Mode: <tspan fontWeight={600} fill={lineMode === 'free' ? '#0EA5E9' : '#16A34A'}>{lineMode === 'free' ? 'Free angle' : 'Horizontal / Vertical snap'}</tspan> · Shift = inverse
                    </text>
                  </g>
                )}
              </svg>
            </div>

            {/* Length popover — positioned absolutely over the canvas */}
            {popover && (
              <div data-testid="flashing-trace-popover" className="relative">
                <div className="absolute z-10 bg-white rounded-md shadow-lg ring-1 ring-slate-200 p-2 flex items-center gap-1.5 text-xs" style={{ left: Math.min(CANVAS_W - 220, Math.max(8, popover.x)), top: Math.max(-CANVAS_H - 8, popover.y - CANVAS_H - 24) }}>
                  <span className="font-bold text-slate-600">F{popover.segmentIndex + 1}</span>
                  <input
                    type="number"
                    step={GRID_MM}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    value={popover.length_mm}
                    onChange={(e) => setPopover({ ...popover, length_mm: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyPopoverLength();
                      if (e.key === 'Escape') setPopover(null);
                    }}
                    className="w-20 text-sm px-1.5 py-1 border border-slate-200 rounded font-mono-num"
                    data-testid="flashing-trace-popover-length"
                  />
                  <span className="text-[10px] text-slate-500">mm</span>
                  <button type="button" onClick={applyPopoverLength} data-testid="flashing-trace-popover-ok" className="px-2 py-1 bg-emerald-600 text-white rounded text-xs font-bold hover:bg-emerald-700">OK</button>
                  <button type="button" onClick={() => setPopover(null)} className="p-1 text-slate-400 hover:text-slate-700"><X size={12} /></button>
                </div>
              </div>
            )}

            {/* Right-click label reposition menu — appears next to the
                clicked badge. Buttons compute the override needed to
                push the badge to a cardinal position (above / below /
                left / right of the segment midpoint, 40 px clear) so
                the operator can line up multiple labels consistently. */}
            {labelMenu && (
              <div
                data-testid="flashing-trace-label-menu"
                className="relative"
                onMouseLeave={() => setLabelMenu(null)}
              >
                <div
                  className="absolute z-20 bg-white rounded-md shadow-lg ring-1 ring-slate-200 py-1 text-xs min-w-[160px]"
                  style={{
                    left: Math.min(CANVAS_W - 170, Math.max(8, labelMenu.x)),
                    top:  Math.max(-CANVAS_H, labelMenu.y - CANVAS_H - 8),
                  }}
                >
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 border-b border-slate-100">F{labelMenu.segIdx + 1} label</div>
                  {[
                    { dir: 'above', label: 'Move above segment', dx: 0,    dy: -40 },
                    { dir: 'below', label: 'Move below segment', dx: 0,    dy:  40 },
                    { dir: 'left',  label: 'Move left of segment',  dx: -50, dy: 0 },
                    { dir: 'right', label: 'Move right of segment', dx:  50, dy: 0 },
                  ].map(({ dir, label, dx, dy }) => (
                    <button
                      key={dir}
                      type="button"
                      data-testid={`flashing-trace-label-menu-${dir}`}
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-700"
                      onClick={() => {
                        // Target: midpoint + cardinal offset
                        const targetLx = labelMenu.cx + dx;
                        const targetLy = labelMenu.cy + dy;
                        const idx = labelMenu.segIdx;
                        setLabelOffsets(prev => {
                          const next = prev.slice();
                          next[idx] = {
                            dx: targetLx - labelMenu.autoLx,
                            dy: targetLy - labelMenu.autoLy,
                          };
                          return next;
                        });
                        setLabelMenu(null);
                      }}
                    >{label}</button>
                  ))}
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button
                      type="button"
                      data-testid="flashing-trace-label-menu-auto"
                      className="w-full text-left px-3 py-1.5 hover:bg-sky-50 text-sky-700 font-semibold"
                      onClick={() => {
                        const idx = labelMenu.segIdx;
                        setLabelOffsets(prev => {
                          const next = prev.slice();
                          next[idx] = { dx: 0, dy: 0 };
                          return next;
                        });
                        setLabelMenu(null);
                      }}
                    >↺ Reset to auto-position</button>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-slate-500 text-[11px]"
                      onClick={() => setLabelMenu(null)}
                    >Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer summary — `bendCount` is the number of physical
                bends (interior vertices). For N points the polyline has
                N-1 straight segments + N-2 bends; the first and last
                vertices are the open ends, not folds. */}
            <div className="mt-2 text-xs text-slate-600 flex items-center gap-3 flex-wrap">
              {(() => {
                const bendCount = Math.max(0, points.length - 2);
                return (
                  <span><span className="font-bold text-slate-800">{points.length}</span> point{points.length === 1 ? '' : 's'} · <span className="font-bold text-slate-800" data-testid="flashing-trace-fold-count">{bendCount}</span> fold{bendCount === 1 ? '' : 's'}</span>
                );
              })()}
              <span>Girth <span className="font-bold text-emerald-700 font-mono-num">{formatLen(girth)} {displayUnit}</span></span>
              <span>Start angle <span className="font-mono-num text-slate-700">{start_angle_deg}°</span></span>
              {finalized && points.length >= 2 && (
                <span data-testid="flashing-trace-status-finalized" className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5 font-semibold text-[11px]">
                  <CheckCircle2 size={11} /> Drawing finished — click any segment to edit
                </span>
              )}
              {!finalized && points.length >= 2 && (
                <span data-testid="flashing-trace-status-drawing" className="text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-full px-2 py-0.5 font-semibold text-[11px]">
                  Still drawing — double-click or press Enter when done
                </span>
              )}
              <button type="button" onClick={() => { setPoints([]); setPopover(null); setFinalized(false); setLabelOffsets([]); }} className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 rounded ring-1 ring-rose-200" data-testid="flashing-trace-clear">
                <Trash2 size={12} /> Clear polyline
              </button>
              {labelOffsets.some(o => o && (o.dx || o.dy)) && (
                <button
                  type="button"
                  data-testid="flashing-trace-reset-labels"
                  onClick={() => setLabelOffsets([])}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-sky-700 hover:bg-sky-50 rounded ring-1 ring-sky-200"
                  title="Reset every label badge back to its auto-positioned spot"
                >
                  Reset label positions
                </button>
              )}
            </div>
          </div>

          {/* SIDEBAR — metadata */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Name *</span>
              <input
                data-testid="flashing-trace-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder='e.g. "Apron — 75/200/75"'
                className="mt-1 w-full text-sm px-2 py-1.5 border border-slate-200 rounded bg-white"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Category</span>
              <select
                data-testid="flashing-trace-category"
                value={category}
                onChange={async (e) => {
                  const v = e.target.value;
                  if (v === '__add_new__') {
                    const raw = window.prompt('New flashing category name:');
                    const name = (raw || '').trim();
                    if (!name) return;
                    // Persist to the server-side flashing_profile dropdown
                    // so future templates and every appraisal sees it.
                    if (api) {
                      try {
                        await api.post('/dropdowns/flashing_profile/add', { value: name });
                        setCategories(prev => prev.includes(name) ? prev : [...prev, name]);
                      } catch (_) {
                        // Even if the API fails (offline), reflect locally so
                        // the operator's current template isn't blocked.
                        setCategories(prev => prev.includes(name) ? prev : [...prev, name]);
                      }
                    } else {
                      setCategories(prev => prev.includes(name) ? prev : [...prev, name]);
                    }
                    setCategory(name);
                    return;
                  }
                  setCategory(v);
                  // Auto-load the starter shape for that category if the
                  // operator hasn't drawn anything yet — saves redrawing
                  // the same backtray / apron / barge pattern every time.
                  if (!polylineDirtyRef.current && STARTER_SHAPES[v]) {
                    loadStarter(v);
                  }
                }}
                className="mt-1 w-full text-sm px-2 py-1.5 border border-slate-200 rounded bg-white"
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__add_new__" data-testid="flashing-trace-category-add">+ Add new category…</option>
              </select>
              {(STARTER_SHAPES[category] || (categoryStarters && categoryStarters[category])) && (
                <button
                  type="button"
                  data-testid="flashing-trace-load-starter"
                  onClick={() => {
                    if (points.length > 0) {
                      if (!window.confirm(`Replace current polyline with the standard "${category}" starter shape?`)) return;
                    }
                    loadStarter(category);
                  }}
                  className="mt-1 w-full text-[11px] font-semibold px-2 py-1 rounded ring-1 ring-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center justify-center gap-1.5"
                  title={`Drop the standard ${category} pattern on the canvas — then click any segment to retype its mm value.`}
                >
                  {categoryStarters && categoryStarters[category] ? '★ ' : ''}Load standard {category} shape
                  {categoryStarters && categoryStarters[category] && (
                    <span className="text-[9px] opacity-70 font-normal">(custom)</span>
                  )}
                </button>
              )}
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Pairs with sheet profile(s)</span>
              {/* All-profiles toggle — saves clicking each chip. When ON,
                  the template is universal (back-end ignores the profile
                  filter for this template); the per-profile chips below
                  are dimmed for visual clarity. Stored on the template
                  as `applies_to_all_profiles: true`. */}
              <label
                data-testid="flashing-trace-all-profiles-toggle"
                className={`mt-1 mb-1.5 flex items-center gap-2 px-2 py-1.5 rounded ring-1 cursor-pointer text-xs select-none ${
                  appliesToAllProfiles
                    ? 'bg-emerald-50 ring-emerald-300 text-emerald-800 font-semibold'
                    : 'bg-white ring-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  data-testid="flashing-trace-all-profiles-checkbox"
                  checked={appliesToAllProfiles}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setAppliesToAllProfiles(next);
                    if (next) setSheetProfilesPicked([]); // clear per-profile selection
                  }}
                />
                <span>All profiles {appliesToAllProfiles && <span className="text-[10px] opacity-70">— template applies universally</span>}</span>
              </label>
              <div
                className={`mt-1 flex flex-wrap gap-1.5 transition-opacity ${appliesToAllProfiles ? 'opacity-40 pointer-events-none' : ''}`}
                data-testid="flashing-trace-sheet-profiles"
              >
                {sheetProfiles.map((p) => {
                  const on = sheetProfilesPicked.includes(p);
                  return (
                    <button
                      type="button"
                      key={p}
                      data-testid={`flashing-trace-sp-chip-${p}`}
                      onClick={() => {
                        setSheetProfilesPicked(prev =>
                          prev.includes(p)
                            ? prev.filter(x => x !== p)
                            : [...prev, p]
                        );
                      }}
                      className={`px-2 py-0.5 text-[11px] rounded-full ring-1 transition-colors ${
                        on
                          ? 'bg-emerald-600 text-white ring-emerald-600'
                          : 'bg-white text-slate-700 ring-slate-300 hover:bg-emerald-50 hover:ring-emerald-300'
                      }`}
                    >
                      {on ? '✓ ' : ''}{p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  data-testid="flashing-trace-sheet-profile-add"
                  onClick={async () => {
                    const raw = window.prompt('New sheet profile name (e.g. "TrimRib 0.91m"):');
                    const sp = (raw || '').trim();
                    if (!sp) return;
                    if (api) {
                      try {
                        await api.post('/dropdowns/sheet_profile/add', { value: sp });
                      } catch (_) {/* still add locally */}
                    }
                    setSheetProfiles(prev => prev.includes(sp) ? prev : [...prev, sp]);
                    setSheetProfilesPicked(prev => prev.includes(sp) ? prev : [...prev, sp]);
                  }}
                  className="px-2 py-0.5 text-[11px] rounded-full ring-1 ring-dashed ring-slate-400 text-slate-500 hover:bg-slate-50"
                >
                  + Add
                </button>
              </div>
              <span className="block mt-1 text-[10px] text-slate-400 italic">
                Optional · tick every profile this flashing fits (one flashing can pair with multiple roof profiles).
              </span>
            </label>
            {/* Pipe-usage auto-link rows — lets this template be the
                auto-suggested flashing when an operator tags a Pipe pin
                on the diagram with a matching usage + diameter combo. */}
            <PipeUsageLinksEditor links={pipeUsageLinks} onChange={setPipeUsageLinks} />
            {/* Edging fitted on polyline endpoints. Some flashings have a
                folded / hemmed / soft edge fitted on the visible ends;
                pick the type per end so the trace drawing accurately
                reflects the supplied product. */}
            <div className="block" data-testid="flashing-trace-edges-editor">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Edging fitted
              </span>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <label className="block">
                  <span className="text-[9px] text-slate-500 uppercase tracking-wide">Start (V1)</span>
                  <select
                    data-testid="flashing-trace-edge-start"
                    value={edges.start || 'None'}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') {
                        const created = await addEdgeType();
                        if (created) setEdges(prev => ({ ...prev, start: created }));
                        return;
                      }
                      setEdges(prev => ({ ...prev, start: v === 'None' ? null : v }));
                    }}
                    className="w-full mt-0.5 px-1.5 py-1 text-[11px] border border-slate-200 rounded bg-white"
                  >
                    <option value="None">No edging</option>
                    {edgeTypes.map(o => (
                      <option key={o.key} value={o.key}>
                        {o.label}{o.rate_per_m ? ` · $${o.rate_per_m}/m` : ''}
                      </option>
                    ))}
                    <option value="__add_new__">+ Add new edge type…</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[9px] text-slate-500 uppercase tracking-wide">End (V{points.length || '—'})</span>
                  <select
                    data-testid="flashing-trace-edge-end"
                    value={edges.end || 'None'}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') {
                        const created = await addEdgeType();
                        if (created) setEdges(prev => ({ ...prev, end: created }));
                        return;
                      }
                      setEdges(prev => ({ ...prev, end: v === 'None' ? null : v }));
                    }}
                    className="w-full mt-0.5 px-1.5 py-1 text-[11px] border border-slate-200 rounded bg-white"
                  >
                    <option value="None">No edging</option>
                    {edgeTypes.map(o => (
                      <option key={o.key} value={o.key}>
                        {o.label}{o.rate_per_m ? ` · $${o.rate_per_m}/m` : ''}
                      </option>
                    ))}
                    <option value="__add_new__">+ Add new edge type…</option>
                  </select>
                </label>
              </div>
              <span className="block mt-1 text-[10px] text-slate-400 italic">
                Optional — adds an edging glyph on the chosen end + pushes a per-meter Edging line to Components when the flashing is used. Rates live in the Edge Types table.
              </span>
              {/* SoftEdge shape & rotation tuning — only surfaces when
                  at least one end has SoftEdge fitted. Lets the
                  operator dial the curl orientation + size + tail +
                  under-depth + back-reach per template so the SE
                  graphic matches the actual physical fold for each
                  flashing profile. Multipliers are stored in
                  edgeConfigs.{start,end}; defaults all = 1.0. */}
              {(edges.start === 'SoftEdge' || edges.end === 'SoftEdge') && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="flashing-trace-se-tuning">
                  {['start', 'end'].map((side) => {
                    if (edges[side] !== 'SoftEdge') return null;
                    const cfg = edgeConfigs[side] || {};
                    const setCfg = (patch) => setEdgeConfigs(prev => ({
                      ...prev,
                      [side]: { ...(prev[side] || {}), ...patch },
                    }));
                    const labelFor = side === 'start' ? 'V1 SE' : `V${points.length || '—'} SE`;
                    return (
                      <div key={side} className="rounded ring-1 ring-cyan-200 bg-cyan-50/60 p-2" data-testid={`flashing-trace-se-tuning-${side}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-900">{labelFor} shape</span>
                          <button
                            type="button"
                            data-testid={`flashing-trace-se-reset-${side}`}
                            onClick={() => setEdgeConfigs(prev => ({ ...prev, [side]: {} }))}
                            className="text-[9px] text-cyan-700 hover:text-cyan-900 underline"
                            title="Reset all sliders to default"
                          >Reset</button>
                        </div>
                        {/* Mirror-aware orientation — operator picks
                            which side of the polyline the curl hangs.
                            Default 'auto' = always DOWN on screen. For
                            vertical-orient flashings (barge etc.)
                            left / right places the curl on the OUTSIDE
                            of the roof rather than below. */}
                        <label className="flex items-center gap-2 text-[10px] text-cyan-800 mb-1">
                          <span className="w-12">Hang</span>
                          <select
                            data-testid={`flashing-trace-se-${side}-hang_dir`}
                            value={cfg.hang_dir || 'auto'}
                            onChange={(e) => setCfg({ hang_dir: e.target.value })}
                            className="flex-1 px-1.5 py-0.5 text-[10px] rounded ring-1 ring-cyan-300 bg-white text-cyan-900"
                          >
                            <option value="auto">Auto (down)</option>
                            <option value="down">Down ↓</option>
                            <option value="up">Up ↑</option>
                            <option value="left">Left ←</option>
                            <option value="right">Right →</option>
                          </select>
                        </label>
                        {[
                          { key: 'angle_deg', label: 'Rotation', min: -180, max: 180, step: 5, suffix: '°', def: 0 },
                          { key: 'size',      label: 'Size',     min: 0.5,  max: 2.0, step: 0.05, suffix: '×', def: 1.0 },
                          { key: 'tail',      label: 'Tail',     min: 0.5,  max: 2.5, step: 0.05, suffix: '×', def: 1.0 },
                          { key: 'under',     label: 'Dip',      min: 0.5,  max: 2.0, step: 0.05, suffix: '×', def: 1.0 },
                          { key: 'back',      label: 'Back',     min: 0.5,  max: 2.0, step: 0.05, suffix: '×', def: 1.0 },
                        ].map(({ key, label, min, max, step, suffix, def }) => {
                          const val = cfg[key] !== undefined ? Number(cfg[key]) : def;
                          return (
                            <label key={key} className="flex items-center gap-2 text-[10px] text-cyan-800">
                              <span className="w-12">{label}</span>
                              <input
                                type="range"
                                min={min}
                                max={max}
                                step={step}
                                value={val}
                                onChange={(e) => setCfg({ [key]: parseFloat(e.target.value) })}
                                data-testid={`flashing-trace-se-${side}-${key}`}
                                className="flex-1 accent-cyan-600"
                              />
                              <span className="w-12 text-right font-mono-num text-cyan-900">
                                {key === 'angle_deg' ? Math.round(val) : val.toFixed(2)}{suffix}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Primary editable folds — for Custom (Cust) flashing
                templates. Author ticks which fold dimensions are the
                "variable" ones every operator must confirm when they
                instance this template on a pin. Empty = all fixed. */}
            <div className="block" data-testid="flashing-trace-primary-folds-editor">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Custom · primary editable folds
              </span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {folds.length === 0 && (
                  <span className="text-[10px] text-slate-400 italic">Draw the polyline first to choose editable folds.</span>
                )}
                {folds.map((f, i) => {
                  const on = primaryEditableFolds.includes(i);
                  return (
                    <button
                      type="button"
                      key={i}
                      data-testid={`flashing-trace-primary-fold-${i}`}
                      onClick={() => setPrimaryEditableFolds(prev =>
                        prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort((a, b) => a - b)
                      )}
                      className={`px-2 py-0.5 text-[11px] rounded-full ring-1 transition-colors ${
                        on
                          ? 'bg-amber-500 text-white ring-amber-500'
                          : 'bg-white text-slate-700 ring-slate-300 hover:bg-amber-50 hover:ring-amber-300'
                      }`}
                      title={`Mark F${i + 1} (${Math.round(f.length_mm || 0)} mm) as variable on Custom instances`}
                    >
                      {on ? '⚙ ' : ''}F{i + 1}
                    </button>
                  );
                })}
              </div>
              <span className="block mt-1 text-[10px] text-slate-400 italic">
                Operators will be prompted to confirm or override these dimensions on every per-pin instance of this template.
              </span>
            </div>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Notes</span>
              <textarea
                data-testid="flashing-trace-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Site reminder, supplier hint, install notes…"
                className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-200 rounded bg-white"
              />
            </label>
            <div className="bg-slate-50 rounded p-2 ring-1 ring-slate-200 text-[11px] text-slate-600 space-y-1">
              <div className="font-bold text-slate-700 uppercase tracking-[0.12em] text-[9px]">Computed folds</div>
              {folds.length === 0 && <div className="italic text-slate-400">Click on the grid to start</div>}
              {folds.map((f, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span className="font-mono-num">F{i + 1}</span>
                  <span className="font-mono-num text-slate-800">{formatLen(f.length_mm)} {displayUnit}</span>
                  {i < folds.length - 1 && (
                    <span className="font-mono-num text-slate-500">{f.bend_angle}° {f.bend_dir === 'cw' ? '↻' : '↺'}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {/* "Save as starter" — only meaningful once the template has
              actually been saved (has an _id). Toggles is_category_starter
              for this template's category: whatever was previously the
              starter for that category gets demoted server-side. */}
          {initial?._id && onSetAsStarter && (
            initial?.is_category_starter ? (
              <button
                type="button"
                data-testid="flashing-trace-unset-starter"
                onClick={() => onUnsetAsStarter?.(initial._id)}
                className="mr-auto px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded hover:bg-amber-100 inline-flex items-center gap-1.5"
                title="Stop using this template as the default starter for its category"
              >
                ★ Default starter — click to unset
              </button>
            ) : (
              <button
                type="button"
                data-testid="flashing-trace-set-starter"
                onClick={() => onSetAsStarter?.(initial._id, category)}
                className="mr-auto px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white ring-1 ring-slate-200 rounded hover:bg-emerald-50 hover:ring-emerald-200 hover:text-emerald-800 inline-flex items-center gap-1.5"
                title={`Make THIS template the default starter shape for the ${category} category. Future ${category} templates will start with this exact polyline.`}
              >
                ☆ Save as starter for {category}
              </button>
            )
          )}
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
          {/* Auto-save status pill — only meaningful for existing templates */}
          {initial?._id && (
            <span
              data-testid="flashing-trace-autosave-status"
              className={`px-2 py-1 text-[10px] font-semibold rounded ring-1 ${
                autoSaveInFlight
                  ? 'text-amber-700 bg-amber-50 ring-amber-200'
                  : lastSavedAt
                    ? 'text-emerald-700 bg-emerald-50 ring-emerald-200'
                    : 'text-slate-500 bg-white ring-slate-200'
              }`}
              title="Auto-saves to the server 1.5 s after the last change"
            >
              {autoSaveInFlight
                ? 'Auto-saving…'
                : lastSavedAt
                  ? `Saved ${secondsAgo(lastSavedAt)}`
                  : 'Auto-save: armed'}
            </span>
          )}
          {/* "Save as new template" — clones the current state under a new
              name so the operator can derive a variant without disturbing
              the original. Only useful once the original has been saved. */}
          {initial?._id && onSaveAsNew && (
            <button
              type="button"
              data-testid="flashing-trace-save-as-new"
              disabled={!canSave || saving}
              onClick={() => {
                const raw = window.prompt('New template name:', `${name.trim()} (copy)`);
                const nm = (raw || '').trim();
                if (!nm) return;
                onSaveAsNew({ ...buildPayload(), _id: undefined, name: nm });
              }}
              className="px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-white ring-1 ring-emerald-300 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed rounded inline-flex items-center gap-1.5"
              title="Save the current shape + dimensions as a brand new template (keep this one too)"
            >
              ＋ Save as new template
            </button>
          )}
          {/* Save (keep editing) — only for existing templates. New
              templates always close on save because we need an explicit
              create step. */}
          {initial?._id && (
            <button
              data-testid="flashing-trace-save-keep"
              disabled={!canSave || saving}
              onClick={async () => {
                try {
                  setAutoSaveInFlight(true);
                  await api.put(`/flashing-templates/${initial._id}`, buildPayload());
                  setLastSavedAt(new Date());
                } finally { setAutoSaveInFlight(false); }
              }}
              className="px-3 py-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-300 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed rounded inline-flex items-center gap-1.5"
              title="Save changes without closing the editor"
            >
              {autoSaveInFlight ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save (keep editing)
            </button>
          )}
          <button
            data-testid="flashing-trace-save"
            disabled={!canSave || saving}
            onClick={() => onSave(buildPayload())}
            className="px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {initial?._id ? 'Save & close' : 'Create template'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers + sub-components
// ────────────────────────────────────────────────────────────────────────
function secondsAgo(date) {
  if (!date) return '';
  const s = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return `${m}m ago`;
}

// Inline edging glyph for the live trace editor canvas. Renders the
// hook directly in screen-pixel space (the editor's SVG isn't using
// FlashingProfileSVG so we duplicate the small bit of geometry math
// here). Always white-tipped at the endpoint so it reads cleanly on
// either colour background.
export const EDGE_TYPES = [
  { key: 'None',     label: 'No edging' },
  { key: 'SoftEdge', label: 'SoftEdge (SE)' },
];

function EdgeGlyphInline({ end, edgeType, x0, y0, xa, ya, config = {} }) {
  if (!edgeType || edgeType === 'None') return null;
  const dx = xa - x0;
  const dy = ya - y0;
  const len = Math.max(1, Math.hypot(dx, dy));
  let tx = dx / len;
  let ty = dy / len;
  const cand1 = { nx:  ty, ny: -tx };
  const cand2 = { nx: -ty, ny:  tx };
  const hangDir = (config.hang_dir || 'auto');
  let pick;
  if (hangDir === 'down') {
    pick = cand1.ny >= cand2.ny ? cand1 : cand2;
  } else if (hangDir === 'up') {
    pick = cand1.ny <= cand2.ny ? cand1 : cand2;
  } else if (hangDir === 'right') {
    pick = cand1.nx >= cand2.nx ? cand1 : cand2;
  } else if (hangDir === 'left') {
    pick = cand1.nx <= cand2.nx ? cand1 : cand2;
  } else {
    pick = cand1.ny > cand2.ny
      ? cand1
      : (cand1.ny === cand2.ny ? (cand1.nx >= cand2.nx ? cand1 : cand2) : cand2);
  }
  let nx = pick.nx;
  let ny = pick.ny;
  // Apply per-template rotation override (degrees, clockwise positive).
  const rotDeg = Number(config.angle_deg) || 0;
  if (rotDeg !== 0) {
    const r = (rotDeg * Math.PI) / 180;
    const cs = Math.cos(r);
    const sn = Math.sin(r);
    const tx2 = tx * cs - ty * sn;
    const ty2 = tx * sn + ty * cs;
    const nx2 = nx * cs - ny * sn;
    const ny2 = nx * sn + ny * cs;
    tx = tx2; ty = ty2; nx = nx2; ny = ny2;
  }
  const sizeMult  = Math.max(0.25, Math.min(2.5, Number(config.size)  || 1.0));
  const tailMult  = Math.max(0.25, Math.min(3.0, Number(config.tail)  || 1.0));
  const underMult = Math.max(0.25, Math.min(2.5, Number(config.under) || 1.0));
  const backMult  = Math.max(0.25, Math.min(2.5, Number(config.back)  || 1.0));
  const SIZE = 22 * sizeMult;
  const toScreen = (t, n) => [x0 + tx * t + nx * n, y0 + ty * t + ny * n];
  const TAIL  = SIZE * 2.8 * tailMult;
  const UNDER = SIZE * 0.8 * underMult;
  const BACK  = SIZE * 0.55 * backMult;
  const c1a = toScreen( SIZE * 0.15, SIZE * 0.20);
  const c1b = toScreen( BACK,        SIZE * 0.55);
  const p1  = toScreen( BACK * 0.65, UNDER);
  const c2a = toScreen( BACK * 0.10, UNDER * 1.10);
  const c2b = toScreen(-TAIL * 0.55, UNDER * 0.95);
  const p2  = toScreen(-TAIL,        UNDER * 0.55);
  const d = (
    `M ${x0} ${y0} ` +
    `C ${c1a[0]} ${c1a[1]} ${c1b[0]} ${c1b[1]} ${p1[0]} ${p1[1]} ` +
    `C ${c2a[0]} ${c2a[1]} ${c2b[0]} ${c2b[1]} ${p2[0]} ${p2[1]}`
  );
  const SE_COLOR = '#0891B2';
  return (
    <g pointerEvents="none">
      <path d={d} fill="none" stroke={SE_COLOR} strokeWidth={2.0} strokeLinecap="round" strokeLinejoin="round" />
      <text
        x={x0 + tx * (-TAIL * 1.05) + nx * (UNDER * 0.15)}
        y={y0 + ty * (-TAIL * 1.05) + ny * (UNDER * 0.15) - 2}
        fontSize={10}
        fontWeight={800}
        fill={SE_COLOR}
        textAnchor="middle"
      >
        {edgeType === 'SoftEdge' ? 'SE' : edgeType.slice(0, 2).toUpperCase()}
      </text>
    </g>
  );
}

// Pipe-usage values surfaced on the diagram annotator + matched against
// `pipe_usage_links` on templates. Kept short + slug-like so the backend
// (which normalises to lowercase underscore) and frontend agree on
// matching keys.
export const PIPE_USAGE_OPTIONS = [
  { key: 'hot_water', label: 'Hot water' },
  { key: 'plumbing_vent', label: 'Plumbing vent' },
  { key: 'stack_vent', label: 'Stack vent' },
  { key: 'overflow', label: 'Overflow' },
  { key: 'gas', label: 'Gas' },
  { key: 'electrical_conduit', label: 'Electrical conduit' },
  { key: 'other', label: 'Other' },
];

function PipeUsageLinksEditor({ links, onChange }) {
  const updateRow = (idx, patch) => onChange(links.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeRow = (idx) => onChange(links.filter((_, i) => i !== idx));
  const addRow = () => onChange([...links, { usage: 'hot_water', pipe_dia_mm: 20, length_default_m: 0.4 }]);
  return (
    <div className="block" data-testid="flashing-trace-pipe-usage-editor">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Auto-link for pipe pins
        </span>
        <button
          type="button"
          data-testid="flashing-trace-pipe-usage-add"
          onClick={addRow}
          className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900"
        >+ Add</button>
      </div>
      {links.length === 0 && (
        <div className="mt-1 text-[10px] text-slate-400 italic">
          Optional · pre-set this template to auto-suggest on Pipe pins matching a usage + diameter combo.
        </div>
      )}
      <div className="mt-1 space-y-1">
        {links.map((l, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`flashing-trace-pipe-usage-row-${i}`}>
            <select
              value={l.usage || ''}
              onChange={(e) => updateRow(i, { usage: e.target.value })}
              className="text-[11px] px-1.5 py-1 border border-slate-200 rounded bg-white flex-1"
            >
              {PIPE_USAGE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <input
              type="number"
              value={l.pipe_dia_mm ?? ''}
              onChange={(e) => updateRow(i, { pipe_dia_mm: e.target.value === '' ? 0 : Number(e.target.value) })}
              placeholder="dia (mm)"
              title="Pipe diameter (mm) — leave 0 to match any"
              className="text-[11px] px-1.5 py-1 border border-slate-200 rounded bg-white w-20"
            />
            <input
              type="number"
              step="0.1"
              value={l.length_default_m ?? ''}
              onChange={(e) => updateRow(i, { length_default_m: e.target.value === '' ? 0 : Number(e.target.value) })}
              placeholder="length m"
              title="Default flashing length when auto-applied (m)"
              className="text-[11px] px-1.5 py-1 border border-slate-200 rounded bg-white w-20"
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-slate-400 hover:text-rose-600 text-sm"
              title="Remove"
            >×</button>
          </div>
        ))}
      </div>
    </div>
  );
}
